import type { ServicioData } from './CardData';
import CardData from './CardData';
import type { CardProperties, CardRawData } from './CardData';
import styles from "../components/CardInfo.module.scss";
import i18n from "../i18n";
import {LocaleService} from "../common/LocaleService";
import SvgIcon from '@material-ui/core/SvgIcon';
import type { SvgIconProps } from '@material-ui/core/SvgIcon';
import Button from '@material-ui/core/Button';
import {withStyles} from '@material-ui/core/styles';
import {lighten, darken} from '@material-ui/core/styles/colorManipulator';
import SentimentSatisfiedOutlinedIcon from '@material-ui/icons/SentimentSatisfiedOutlined';
import SentimentDissatisfiedOutlinedIcon from '@material-ui/icons/SentimentDissatisfiedOutlined';
import {ModalComponentService} from "../common/ModalComponentService";
import IconReportError from '../icons/icon_report_error.svg?react';

/** Fila de precio: importe y clave del tipo de habitacion. */
export interface RoomPrice {
    first: string;
    second: string;
}

/** Valoraciones agregadas del alojamiento. */
export interface HotelRating {
    options?: Record<string, string | number>;
    up?: string | number;
    down?: string | number;
    none?: string | number;
    count?: string | number;
    up_percent?: string | number;
    down_percent?: string | number;
}

/** Datos crudos de un alojamiento, tal cual llegan del detalle REST. */
export interface HotelCardRawData extends CardRawData {
    servicios_albergue?: ServicioData[];
    contact_phone?: string[];
    admite_reserva?: string;
    disponibilidad_meses_inclu?: string;
    room_price: RoomPrice[];
    observaciones?: string;
    room_notes?: string;
    localidad?: { name?: string };
    hotel_rating?: HotelRating;
}

const ReportErrorButton = withStyles((theme) => ({
    root: {
        marginTop: 12,
        backgroundColor: lighten(theme.palette.warning.main, 0.9),
        color: darken(theme.palette.warning.main, 0.6),
        '&:hover': {
            backgroundColor: lighten(theme.palette.warning.main, 0.9),
        },
        '& .MuiSvgIcon-root': {
            color: theme.palette.warning.main,
            fontSize: 22,
            opacity: 0.9,
        },
    },
}))(Button);

function SentimentNeutralIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props}>
            <path d="M9 15.5h6v1H9z"></path><circle cx="15.5" cy="9.5" r="1.5"></circle><circle cx="8.5" cy="9.5" r="1.5"></circle><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8"></path>
        </SvgIcon>
    );
}

/**
 * CardData - City, Hotel
 *
 * Data wrapper for object types:
 * - Accommodation (Hotel, Hostel)
 * - Place (City, Monument)
 */
class HotelCard extends CardData {
    // Solo estrecha el tipo del campo de CardData: `declare` no emite nada.
    declare data: HotelCardRawData;

    // Ningun campo de CardData se redeclara aqui: sus inicializadores son los
    // mismos y volver a declararlos solo los reinicia despues de super(), que
    // es justo cuando CardData ya ha asignado bookingUrl desde data.
    // El constructor de abajo asigna lo que este tipo necesita.

    static priceIcons: Record<string, string> = {
        places_shared: 'user-structure',
        rooms_shared: 'list',
        rooms_private: 'bedroom-nightstand',
        room_2: 'parents',
        room_3: 'group',
        room_4: 'dice-4',
        appartments: 'home',
        bungalow: 'home',
        bungalows: 'home',
        house: 'home',
        houses: 'home',
        appartment: 'home',
        room_0: 'user-structure',
        person_guest: 'user-structure',
        room_1: 'user',
        person: 'user',
        room_0_breakfast: 'user-structure, coffee-cup',
        room_0_dinner: 'user-structure, dining-set',
        room_0_full: 'user-structure, plate-of-food',
        person_pilgrim: 'person-walking',
        warning: 'warning-sign',
        working: 'construction-cone',
        obras: 'construction-cone',
        closed: 'ban-circle'
    };

    constructor(type: string, properties: CardProperties, data: HotelCardRawData) {
      super(type, properties, data);

      this.title = data.title;
      
      if (data.servicios_albergue) {
          this.tag_servicios = data.servicios_albergue;
      } 
                  
      if (data.contact_phone && (data.contact_phone.length > 0)) {
          this.phoneNumber = data.contact_phone[0].replace(/\s/g, "");
      }                          

      this.geo_location = data.geo_location;
      this.bookingUrl = data.booking_url;
    }
    
    /**
     * Returns hotel[id]
     */
    getMapId(): string {
      return 'hotel' + this.id;
    }    
    
    /**
     * When closed, message is in 'status_warning'.
     */
    getWarningType(): string {
      if (this.data && this.data.status_warning) {
        return HotelCard.WARNING_CLOSED;
      }
      else {
        return super.getWarningType();
      }
    }

    /**
     * returns the price of the hotel normalized
     */
    static normalizePrice(price: string): string {
        if (price === '0') {
            return i18n.t('cardContent.hotel.price.donation');
        } else if (price.includes('0 ')) {
            const priceWord = price.substring(2);
            return priceWord.toLowerCase() === 'gratis' ? i18n.t('cardContent.hotel.price.gratis') : priceWord;
        } else if (!isNaN(Number(price))) {
            return price + ' €';
        }
        return price + ' €';
    }

    static getCardDOM (cardData: HotelCard) {
        const {admite_reserva, disponibilidad_meses_inclu, room_price, observaciones, room_notes} = cardData.data;
        let filteredAdmiteReserva = admite_reserva;
        if (LocaleService.filterLangValue(i18n.language) === 'en' && admite_reserva) {
            filteredAdmiteReserva = admite_reserva === 'Yeah' ? 'Yes' : admite_reserva;
        }
        const priceLabel = cardData.data.type === 'albergues' ?
            i18n.t('cardContent.hotel.price.albergue') : i18n.t('cardContent.hotel.price.alojamiento');
        let prices;
        if (room_price.length > 0) {
            prices = <div>
                <div className={styles.label}>{priceLabel}</div>
                <div className={styles.priceTable}>
                    {
                        room_price.map((price, index) => {
                            const icons = this.priceIcons[price.second].split(',').map((icon, index) =>
                                <i key={`${price.second}_${index}`} className={`glyphicons glyphicons-${icon.trim()}`}/>
                            );
                            return <div key={index} className={styles.priceRow}>
                                <span>{this.normalizePrice(price.first)}</span>
                                <span>
                                    { icons }
                                    {i18n.t(`cardContent.hotel.rooms.${price.second}`)}
                                </span>
                            </div>
                        })
                    }
                </div>
            </div>
        }
        let reservation;
        if (admite_reserva) {
            reservation = <div>
                <div className={styles.label}>{i18n.t('cardContent.hotel.reservation')}</div>
                {filteredAdmiteReserva && LocaleService.decodeHtmlEntities(filteredAdmiteReserva)}
            </div>
        }
        let availability;
        if (disponibilidad_meses_inclu) {
            availability = <div>
                <div className={styles.label}>{i18n.t('cardContent.hotel.availability')}</div>
                {disponibilidad_meses_inclu && LocaleService.decodeHtmlEntities(disponibilidad_meses_inclu)}
            </div>
        }
        let observations;
        if (observaciones || room_notes) {
            observations = <div>
                <div className={styles.label}>{i18n.t('cardContent.hotel.observations')}</div>
                {observaciones && LocaleService.decodeHtmlEntities(observaciones)}
                {room_notes && <div>{LocaleService.decodeHtmlEntities(room_notes)}</div>}
            </div>
        }

        // El botón de reportar error ahora siempre se muestra
        const reportErrorButton = (
            <ReportErrorButton
                variant="contained"
                fullWidth
                startIcon={<SvgIcon component={IconReportError} viewBox="0 0 24 24" />}
                onClick={() => {
                    const localityName = cardData?.data?.localidad?.name;
                    const cardTitle = cardData?.title || null;

                    const reportErrorEmailSubject = localityName
                        ? i18n.t('reportError.hotelEmailSubject', { title: cardTitle, location: localityName })
                        : null;

                    const reportErrorWhatsappMessage = localityName
                        ? i18n.t('reportError.hotelWhatsappSubject', { title: cardTitle, location: localityName })
                        : null;

                    ModalComponentService.openModalComponent(
                        ModalComponentService.ModalComponentTypes.REPORT_ERROR_CARD,
                        {
                            reportErrorEmailSubject,
                            reportErrorWhatsappMessage,
                            reportErrorCardTitle: cardTitle
                        }
                    );
                }}
            >
                {i18n.t('menu.error')}
            </ReportErrorButton>
        );

        return <div>
            <div className={styles.hotelTable}>
                {prices}
                {reservation}
                {availability}
                {observations}
                {reportErrorButton}
            </div>
        </div>;
    }

    getRatingColorbarDOM() {
        const rating = this.data?.hotel_rating;
        if (!rating) {
            return null;
        }
        const options = rating.options;
        if (options) {
            const optionUp = Number(options['1']) || 0;
            const optionNone = Number(options['0']) || 0;
            const optionDown = Number(options['-1']) || 0;
            if (optionUp === 0 && optionNone === 0 && optionDown === 0) {
                return null;
            }
        }

        const upCount = Number(rating.up) || 0;
        const downCount = Number(rating.down) || 0;
        const noneCount = Number(rating.none) || 0;
        const totalCount = Number(rating.count) || (upCount + downCount + noneCount);

        let upPercent = Number(rating.up_percent);
        let downPercent = Number(rating.down_percent);
        if (Number.isNaN(upPercent) || Number.isNaN(downPercent)) {
            if (totalCount > 0) {
                upPercent = Math.round((upCount * 100) / totalCount);
                downPercent = Math.round((downCount * 100) / totalCount);
            } else {
                upPercent = 0;
                downPercent = 0;
            }
        }

        upPercent = Math.max(0, Math.min(100, upPercent));
        downPercent = Math.max(0, Math.min(100 - upPercent, downPercent));
        const nonePercent = Math.max(0, 100 - upPercent - downPercent);

        return (
            <div className={styles.hotelRating}>
                <div className={styles.hotelRatingBar}>
                    <div
                        className={`${styles.hotelRatingSegment} ${styles.hotelRatingSegmentUp}`}
                        style={{width: `${upPercent}%`}}
                    >
                        {upCount > 0 && upPercent >= 10 && (
                            <>
                                <SentimentSatisfiedOutlinedIcon className={styles.hotelRatingIcon} />
                                {upPercent >= (400 / 30) && <span className={styles.hotelRatingCount}>{upCount}</span>}
                            </>
                        )}
                    </div>
                    <div
                        className={`${styles.hotelRatingSegment} ${styles.hotelRatingSegmentNone}`}
                        style={{width: `${nonePercent}%`}}
                    >
                        {noneCount > 0 && nonePercent >= 10 && (
                            <>
                                <SentimentNeutralIcon className={styles.hotelRatingIcon} />
                                {nonePercent >= (400 / 30) && <span className={styles.hotelRatingCount}>{noneCount}</span>}
                            </>
                        )}
                    </div>
                    <div
                        className={`${styles.hotelRatingSegment} ${styles.hotelRatingSegmentDown}`}
                        style={{width: `${downPercent}%`}}
                    >
                        {downCount > 0 && downPercent >= 10 && (
                            <>
                                <SentimentDissatisfiedOutlinedIcon className={styles.hotelRatingIcon} />
                                {downPercent >= (400 / 30) && <span className={styles.hotelRatingCount}>{downCount}</span>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default HotelCard;
